// src/MainMenu.jsx
import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useLoader, useThree } from "@react-three/fiber";
import {
  Billboard,
  Float,
  OrthographicCamera,
  Outlines,
  ScrollControls,
  useScroll,
  useTexture,
} from "@react-three/drei";

import Chain from "../public/biteback/models/Chain2White.jsx";
import UpperTeeth from "../public/biteback/models/UpperTeethWhite.jsx";
import Jaw from "../public/biteback/models/JawWhite.jsx";

const BASE = import.meta.env.BASE_URL;
const TWO_PI = Math.PI * 2;

/* -----------------------------------------------------------------------------
 * Scene elements
 * -------------------------------------------------------------------------- */

function RotatingSpriteStackSheet({
  center = [0, 0, 0],
  radius = 3,
  height = 1.5,
  speed = 0.5,
  startAngle = 0,
  freeze = false,

  sheetA,
  sheetB,
  sheetC,
  sheetAa,

  columns = 8,
  rows = 1,
  planeWidth = 1,
  planeHeight = 1.4,
  billRotation = [-Math.PI / 4, -Math.PI / 6, Math.PI / 6],

  focusRef,
  activeFocus = 0,
  focusId = 1,
}) {
  const pivotRef = useRef(null);
  const angleRef = useRef(startAngle);

  const isActive = activeFocus === 0 || activeFocus === focusId;

  const texA = sheetA ? useLoader(THREE.TextureLoader, sheetA) : null;
  const texB = sheetB ? useLoader(THREE.TextureLoader, sheetB) : null;
  const texC = sheetC ? useLoader(THREE.TextureLoader, sheetC) : null;
  const texAa = sheetAa ? useLoader(THREE.TextureLoader, sheetAa) : null;

  useEffect(() => {
    const initSheet = (tex) => {
      if (!tex) return;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1 / columns, 1 / rows);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.generateMipmaps = false;
      tex.anisotropy = 1;
      tex.needsUpdate = true;
    };

    initSheet(texA);
    initSheet(texB);
    initSheet(texC);
    initSheet(texAa);
  }, [texA, texB, texC, texAa, columns, rows]);

  useFrame((_, dt) => {
    if (!pivotRef.current) return;

    if (!freeze) angleRef.current += dt * speed;

    const angle = angleRef.current;
    pivotRef.current.rotation.y = angle;

    // Convert current rotation angle to a spritesheet frame index.
    const a = ((angle % TWO_PI) + TWO_PI) % TWO_PI;
    const totalFrames = columns * rows;
    const frameFloat = (a / TWO_PI) * totalFrames;
    const baseIndex = Math.floor(frameFloat) % totalFrames;

    // Invert index so it matches your visual direction.
    const frameIndex = (totalFrames - 1 - baseIndex + totalFrames) % totalFrames;

    const col = frameIndex % columns;
    const row = Math.floor(frameIndex / columns);
    const u = col / columns;
    const v = 1 - (row + 1) / rows;

    if (texA) texA.offset.set(u, v);
    if (texB) texB.offset.set(u, v);
    if (texC) texC.offset.set(u, v);
    if (texAa) texAa.offset.set(u, v);
  });

  return (
    <group ref={pivotRef} position={center}>
      {/* Orbiting child (camera focus target) */}
      <group
        position={[radius, height, 0]}
        ref={(node) => {
          if (focusRef) focusRef.current = node;
        }}
      >
        <pointLight intensity={2} color="orange" position={[0, 0, 0.5]} />

        {/* Back layer */}
        {texC && (
          <Billboard>
            <mesh position={[0, 0, 0.02]} receiveShadow rotation={billRotation}>
              <planeGeometry args={[planeWidth, planeHeight]} />
              <meshStandardMaterial
                map={texC}
                transparent
                depthWrite
                depthTest
                opacity={isActive ? 1 : 0.35}
              />
            </mesh>
          </Billboard>
        )}

        {/* Mid layer */}
        {texB && (
          <Billboard>
            <mesh position={[0, 0, 0]} receiveShadow rotation={billRotation}>
              <planeGeometry args={[planeWidth, planeHeight]} />
              <meshStandardMaterial
                map={texB}
                transparent
                depthWrite
                depthTest
                opacity={isActive ? 1 : 0.35}
              />
            </mesh>
          </Billboard>
        )}

        {/* Front layer */}
        {texA && (
          <Billboard>
            <group rotation={billRotation}>
              <mesh position={[0, 0, -0.02]} receiveShadow>
                <planeGeometry args={[planeWidth, planeHeight]} />
                <meshStandardMaterial
                  map={texA}
                  transparent
                  depthWrite
                  depthTest
                  opacity={isActive ? 1 : 0.35}
                />
              </mesh>

              {/* Invisible shadow caster */}
              <mesh
                position={[-0.4, 0, 0]}
                rotation={[Math.PI / 6, -Math.PI / 2, -Math.PI / 4]}
                castShadow
              >
                <planeGeometry args={[planeWidth, planeHeight]} />
                <meshStandardMaterial
                  map={texA}
                  alphaMap={texAa}
                  transparent
                  opacity={0}
                  colorWrite={false}
                  alphaTest={0.5}
                  depthWrite={false}
                />
              </mesh>
            </group>
          </Billboard>
        )}
      </group>
    </group>
  );
}

function PokerChip({
  center = [0, 0, 0],
  radius = 3,
  height = 9,
  orbitSpeed = 0.8,
  orbitOffset = 0,
  spin = 2,
  focusRef,
  activeFocus = 0,
  focusId = 2,
}) {
  const orbitRef = useRef(null);
  const chipRef = useRef(null);

  const tex = useTexture(`${BASE}ganbaru/blenderish.png`);
  useEffect(() => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
  }, [tex]);

  const isActive = activeFocus === 0 || activeFocus === focusId;

  const chipMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: tex,
        color: 0xffffff,
        roughness: 0.3,
        metalness: 0.2,
        transparent: true,
      }),
    [tex]
  );

  // Only update opacity when focus changes (don’t recreate material).
  useEffect(() => {
    chipMat.opacity = isActive ? 1 : 0.5;
    chipMat.needsUpdate = true;
  }, [chipMat, isActive]);

  useFrame(({ clock }, dt) => {
    const t = clock.getElapsedTime();
    const angle = orbitOffset + t * orbitSpeed;
    const [cx, cy, cz] = center;

    const x = cx + radius * Math.cos(angle);
    const z = cz + radius * Math.sin(angle);
    const y = cy + height;

    orbitRef.current?.position.set(x, y, z);

    if (chipRef.current) {
      const wobble = (Math.sin(dt * 10) / 10) * spin;
      chipRef.current.rotation.z += wobble;
      chipRef.current.rotation.x += wobble;
    }
  });

  return (
    <group
      ref={(node) => {
        orbitRef.current = node;
        if (focusRef) focusRef.current = node;
      }}
    >
      <pointLight intensity={10} color="#e91b66" position={[0, 0, 1]} />

      <mesh ref={chipRef} material={chipMat} castShadow receiveShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.2, 9]} />
        {isActive && <Outlines thickness={0.05} color="black" screenspace />}
      </mesh>
    </group>
  );
}

function WhiteBite({
  radius = 2,
  orbitSpeed = 0.5,
  orbitOffset = 0,
  center = [0, 0, 0],
  chompSpeed = 2,
  addRotation = -Math.PI / 2,

  focusRef,
  activeFocus = 0,
  focusId = 3,
}) {
  const pivotRef = useRef(null);
  const biteRootRef = useRef(null);
  const upperRef = useRef(null);
  const jawRef = useRef(null);

  const neutralUpper = { posY: 0, posZ: 0, rotX: 0 };
  const keyUpper = { posY: 0.2, posZ: 0.1, rotX: -Math.PI / 4 };

  const neutralJaw = { posY: 0, posZ: 0, rotX: 0 };
  const keyJaw = { posY: -0.2, posZ: -0.1, rotX: Math.PI / 4 };

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();

    if (pivotRef.current) {
      pivotRef.current.rotation.y = orbitOffset + t * orbitSpeed;
    }

    if (biteRootRef.current) {
      biteRootRef.current.rotation.y = addRotation;
    }

    const a = (Math.sin(t * chompSpeed) + 1) / 2;

    if (upperRef.current) {
      upperRef.current.position.y = THREE.MathUtils.lerp(neutralUpper.posY, keyUpper.posY, a);
      upperRef.current.position.z = THREE.MathUtils.lerp(neutralUpper.posZ, keyUpper.posZ, a);
      upperRef.current.rotation.x = THREE.MathUtils.lerp(neutralUpper.rotX, keyUpper.rotX, a);
    }

    if (jawRef.current) {
      jawRef.current.position.y = THREE.MathUtils.lerp(neutralJaw.posY, keyJaw.posY, a);
      jawRef.current.position.z = THREE.MathUtils.lerp(neutralJaw.posZ, keyJaw.posZ, a);
      jawRef.current.rotation.x = THREE.MathUtils.lerp(neutralJaw.rotX, keyJaw.rotX, a);
    }
  });

  return (
    <group ref={pivotRef} position={center}>
      <group
        ref={(node) => {
          biteRootRef.current = node;
          if (focusRef) focusRef.current = node;
        }}
        position={[radius, 0, 0]}
      >
        <pointLight intensity={20} color="#0f6bce" position={[0, 0, 0]} />

        <group ref={upperRef}>
          <UpperTeeth />
        </group>

        <group ref={jawRef}>
          <Jaw />
        </group>

        <Float speed={5} floatingRange={[-0.3, -0.1]}>
          <Chain
            scale={[0.1, 0.1, 0.1]}
            rotation={[0, (-3 * Math.PI) / 2, 0]}
            position={[0, 0.5, -1]}
          />
        </Float>
      </group>
    </group>
  );
}

function CurvedPanels({
  radius = 1.2,
  height = 1.6,
  count = 3,
  speed = 0.5,
  position = [0, 0.7, 0],
}) {
  const groupRef = useRef(null);

  const geometry = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, height, 64, 1, true, 0, Math.PI / 4),
    [radius, height]
  );

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "white",
        metalness: 0.1,
        roughness: 0.3,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      }),
    []
  );

  useFrame((_, dt) => {
    if (groupRef.current) groupRef.current.rotation.y += dt * speed;
  });

  return (
    <group ref={groupRef} position={position}>
      {Array.from({ length: count }).map((_, i) => (
        <mesh
          key={i}
          geometry={geometry}
          material={material}
          rotation={[0, (i / count) * Math.PI * 2, 0]}
          castShadow
          receiveShadow
        />
      ))}
    </group>
  );
}

function StackedCylindersHitBox() {
  const scroll = useScroll();

  const geometry = useMemo(() => new THREE.CylinderGeometry(1, 1, 1, 8), []);
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        opacity: 0,
        transparent: true,
        depthWrite: false,
        depthTest: true,
      }),
    []
  );

  const segments = [
    { height: 3, radius: 0.1 }, // 0: top cap (no click)
    { height: 5, radius: 5 }, // 1: focus #1 (bite)
    { height: 6, radius: 4 }, // 2: focus #2 (chip)
    { height: 4, radius: 3 }, // 3: focus #3 (sprite)
    { height: 3, radius: 0.1 }, // 4: bottom cap (no click)
  ];

  // normalized scroll anchors (must match camera logic)
  const anchors = useMemo(() => [0, 1 / 3, 2 / 3, 1], []);

  const segmentToAnchorIndex = useMemo(
    () => ({
      1: 3, // bite
      2: 2, // chip
      3: 1, // sprite
    }),
    []
  );

  const handleClick = (segIndex) => {
    const anchorIndex = segmentToAnchorIndex[segIndex];
    if (anchorIndex == null) return;

    const el = scroll.el;
    if (!el) return;

    const maxScrollTop = el.scrollHeight - el.clientHeight;
    el.scrollTop = anchors[anchorIndex] * maxScrollTop;
  };

  let yOffset = 0;
  const placed = segments.map((seg) => {
    const y = yOffset + seg.height / 2;
    yOffset += seg.height;
    return { ...seg, y };
  });

  return (
    <>
      {placed.map((seg, i) => (
        <mesh
          key={i}
          geometry={geometry}
          material={material}
          position={[0, seg.y, 0]}
          scale={[seg.radius, seg.height, seg.radius]}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(i);
          }}
          onPointerOver={(e) => {
            const anchorIndex = segmentToAnchorIndex[i];
            if (anchorIndex != null) {
              e.stopPropagation();
              document.body.style.cursor = "pointer";
            }
          }}
          onPointerOut={() => {
            document.body.style.cursor = "auto";
          }}
        />
      ))}
    </>
  );
}

function StackedCylinders({
  color = "white",
  opacity = 1,
  transparent = opacity < 1,
  rotationSpeed = -0.2, // radians/sec
}) {
  const groupRef = useRef(null);

  const geometry = useMemo(() => new THREE.CylinderGeometry(1, 1, 1, 32), []);
  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color,
        opacity,
        transparent,
      }),
    [color, opacity, transparent]
  );

  const segments = useMemo(
    () => [
      { height: 6, radius: 1.6 },
      { height: 5, radius: 1.3 },
      { height: 3, radius: 1 },
      { height: 3, radius: 1.4 },
      { height: 2, radius: 0.9 },
      { height: 1.5, radius: 0.1 },
      { height: 0.5, radius: 0.6 },
    ],
    []
  );

  let yOffset = 0;
  const placed = segments.map((seg) => {
    const y = yOffset + seg.height / 2;
    yOffset += seg.height;
    return { ...seg, y };
  });

  useFrame((_, dt) => {
    if (groupRef.current) groupRef.current.rotation.y += rotationSpeed * dt;
  });

  return (
    <group ref={groupRef}>
      {placed.map((seg, i) => (
        <mesh
          key={i}
          geometry={geometry}
          material={material}
          position={[0, seg.y, 0]}
          scale={[seg.radius, seg.height, seg.radius]}
          castShadow
          receiveShadow
        />
      ))}
    </group>
  );
}

/* -----------------------------------------------------------------------------
 * Camera + UI hit targets
 * -------------------------------------------------------------------------- */

function ScrollMultiOrthoCamera({
  spriteRef,
  chipRef,
  biteRef,
  scales = [1.2, 2, 1.2, 1.3],
  zoomMult = 0.8,
  onFocusChange,
  camOffsets: camOffsetsProp,
}) {
  const camRef = useRef(null);
  const scroll = useScroll();
  const { size } = useThree();

  const tRef = useRef(0);
  const zoomFactorRef = useRef(scales[0] ?? 1);
  const lastFocusRef = useRef(0);

  const anchors = useMemo(() => [0, 1 / 3, 2 / 3, 1], []);

  const baseZoom = useMemo(() => {
    const minDim = Math.min(size.width, size.height);
    const z = (minDim / 800) * 100 * zoomMult;
    return THREE.MathUtils.clamp(z, 40, 160);
  }, [size.width, size.height, zoomMult]);

  const camOffsets = useMemo(() => {
    if (camOffsetsProp && camOffsetsProp.length === 4) {
      return camOffsetsProp.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    }
    return [
      new THREE.Vector3(-2, 2.5, 2), // hero
      new THREE.Vector3(4, 4, 2), // sprite
      new THREE.Vector3(7, 10, 8), // chip
      new THREE.Vector3(4, 4, 2), // bite
    ];
  }, [camOffsetsProp]);

  const heroPos = useMemo(() => new THREE.Vector3(5, 7, 5), []);
  const heroLook = useMemo(() => new THREE.Vector3(0, 1.5, 0), []);
  const worldOffset = useMemo(() => new THREE.Vector3(5, 7, 5), []);

  const spritePosRef = useRef(new THREE.Vector3());
  const spriteLookRef = useRef(new THREE.Vector3());
  const chipPosRef = useRef(new THREE.Vector3());
  const chipLookRef = useRef(new THREE.Vector3());
  const bitePosRef = useRef(new THREE.Vector3());
  const biteLookRef = useRef(new THREE.Vector3());

  const currentPosRef = useRef(new THREE.Vector3());
  const currentLookRef = useRef(new THREE.Vector3());
  const currentOffsetRef = useRef(new THREE.Vector3());

  useFrame((_, dt) => {
    if (!camRef.current) return;

    const raw = scroll.offset;

    // Snap to nearest anchor when close enough.
    let nearest = anchors[0];
    let minDist = Math.abs(raw - anchors[0]);
    for (let i = 1; i < anchors.length; i += 1) {
      const d = Math.abs(raw - anchors[i]);
      if (d < minDist) {
        minDist = d;
        nearest = anchors[i];
      }
    }

    const snapRadius = 0.12;
    const snapStrength = 4;

    let targetT = raw;
    if (minDist < snapRadius) targetT = nearest;

    tRef.current = THREE.MathUtils.damp(tRef.current, targetT, snapStrength, dt);
    const t = tRef.current;

    // Determine nearest anchor index for focus notifications + zoom.
    let nearestIndex = 0;
    minDist = Math.abs(t - anchors[0]);
    for (let i = 1; i < anchors.length; i += 1) {
      const d = Math.abs(t - anchors[i]);
      if (d < minDist) {
        minDist = d;
        nearestIndex = i;
      }
    }

    if (nearestIndex !== lastFocusRef.current) {
      lastFocusRef.current = nearestIndex;
      onFocusChange?.(nearestIndex);
    }

    // Zoom factor eases to per-section scale.
    const targetZoomFactor = scales[nearestIndex] ?? 1;
    zoomFactorRef.current = THREE.MathUtils.damp(zoomFactorRef.current, targetZoomFactor, 4, dt);

    // Segment selection: 0 (hero→sprite), 1 (sprite→chip), 2 (chip→bite)
    const sections = 3;
    const segLen = 1 / sections;

    let seg = 0;
    let lt = 0;

    if (t < segLen) {
      seg = 0;
      lt = t / segLen;
    } else if (t < segLen * 2) {
      seg = 1;
      lt = (t - segLen) / segLen;
    } else {
      seg = 2;
      lt = (t - segLen * 2) / segLen;
    }

    // Resolve targets from refs.
    const spritePos = spritePosRef.current;
    const spriteLook = spriteLookRef.current;
    const chipPos = chipPosRef.current;
    const chipLook = chipLookRef.current;
    const bitePos = bitePosRef.current;
    const biteLook = biteLookRef.current;

    spritePos.set(0, 8, 8);
    spriteLook.set(0, 15, 0);
    if (spriteRef?.current) {
      spriteRef.current.getWorldPosition(spritePos);
      spriteLook.copy(spritePos);
      spritePos.add(worldOffset);
    }

    chipPos.set(0, 6, 8);
    chipLook.set(0, 2, 0);
    if (chipRef?.current) {
      chipRef.current.getWorldPosition(chipPos);
      chipLook.copy(chipPos);
      chipPos.add(worldOffset);
    }

    bitePos.set(0, 6, 6);
    biteLook.set(0, 4, 0);
    if (biteRef?.current) {
      biteRef.current.getWorldPosition(bitePos);
      biteLook.copy(bitePos);
      bitePos.add(worldOffset);
    }

    let fromPos, toPos, fromLook, toLook, fromOffset, toOffset;

    if (seg === 0) {
      fromPos = heroPos;
      toPos = spritePos;
      fromLook = heroLook;
      toLook = spriteLook;
      fromOffset = camOffsets[0];
      toOffset = camOffsets[1];
    } else if (seg === 1) {
      fromPos = spritePos;
      toPos = chipPos;
      fromLook = spriteLook;
      toLook = chipLook;
      fromOffset = camOffsets[1];
      toOffset = camOffsets[2];
    } else {
      fromPos = chipPos;
      toPos = bitePos;
      fromLook = chipLook;
      toLook = biteLook;
      fromOffset = camOffsets[2];
      toOffset = camOffsets[3];
    }

    const smooth = lt * lt * (3 - 2 * lt);

    const currentPos = currentPosRef.current;
    const currentLook = currentLookRef.current;
    const currentOffset = currentOffsetRef.current;

    currentPos.lerpVectors(fromPos, toPos, smooth);
    currentLook.lerpVectors(fromLook, toLook, smooth);
    currentOffset.lerpVectors(fromOffset, toOffset, smooth);

    currentPos.add(currentOffset);
    currentLook.add(currentOffset);

    camRef.current.position.copy(currentPos);
    camRef.current.lookAt(currentLook);

    camRef.current.zoom = baseZoom * zoomFactorRef.current;
    camRef.current.updateProjectionMatrix();
  });

  return (
    <OrthographicCamera
      ref={camRef}
      makeDefault
      zoom={baseZoom * (scales[0] ?? 1)}
      near={0.01}
      far={1000}
      position={[5, 7, 5]}
    />
  );
}

function BackPlane() {
  const scroll = useScroll();

  const handleClick = (e) => {
    e.stopPropagation();
    const el = scroll.el;
    if (!el) return;
    el.scrollTop = 0;
  };

  return (
    <mesh position={[-25, -20, -20]} rotation={[0, Math.PI / 4, 0]} onClick={handleClick}>
      <planeGeometry args={[30, 60]} />
      <meshStandardMaterial transparent opacity={0} />
    </mesh>
  );
}

/* -----------------------------------------------------------------------------
 * Main scene export
 * -------------------------------------------------------------------------- */

export default function MainScene({
  scales,
  camOffsets,
  setActiveFocus,
  spriteFocusRef,
  chipFocusRef,
  biteFocusRef,
  sceneGroupRef,
  activeFocus,
}) {
  return (
    <>
      <ambientLight intensity={0.5} color="#ffffff" />

      <directionalLight
        position={[5, 10, 2]}
        intensity={6}
        color="#f2feff"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-near={1}
        shadow-camera-far={40}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-bias={-0.0005}
      />

      <ScrollControls pages={2} damping={0.2}>
        <ScrollMultiOrthoCamera
          spriteRef={spriteFocusRef}
          chipRef={chipFocusRef}
          biteRef={biteFocusRef}
          scales={scales}
          onFocusChange={setActiveFocus}
          camOffsets={camOffsets}
        />

        <BackPlane />

        <group
          ref={sceneGroupRef}
          position={[0, -3.5, -2]}
          rotation={[0, Math.PI / 3, Math.PI / 6]}
        >
          <RotatingSpriteStackSheet
            radius={2.2}
            height={1}
            speed={-2}
            startAngle={-0.6}
            sheetA={`${BASE}cybos/body.png`}
            sheetB={`${BASE}cybos/eye.png`}
            sheetC={`${BASE}cybos/mouth.png`}
            sheetAa={`${BASE}cybos/alphaRow.png`}
            columns={8}
            rows={1}
            center={[0, 15, 0]}
            focusRef={spriteFocusRef}
            activeFocus={activeFocus}
            focusId={1}
          />

          <PokerChip
            center={[0, 0, 0]}
            radius={3.2}
            height={10}
            orbitSpeed={1.5}
            orbitOffset={0}
            focusRef={chipFocusRef}
            activeFocus={activeFocus}
            focusId={2}
          />

          <WhiteBite
            radius={3.5}
            orbitSpeed={-2}
            orbitOffset={0}
            center={[0, 4, 0]}
            chompSpeed={12}
            addRotation={0}
            focusRef={biteFocusRef}
            activeFocus={activeFocus}
            focusId={3}
          />

          <StackedCylinders rotationSpeed={-0.3} />
          <StackedCylindersHitBox />

          <CurvedPanels radius={3} height={0.5} count={5} speed={0.4} position={[0, 18, 0]} />
          <CurvedPanels radius={4} height={4} count={3} speed={0.6} />
          <CurvedPanels radius={1.6} height={1} count={5} speed={-0.3} position={[0, -0.45, 0]} />
        </group>
      </ScrollControls>
    </>
  );
}
